// src/pages/RegisterPage.jsx

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  AlertTitle,
  Switch,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    // básicos
    nome: '',
    username: '',
    email: '',
    senha: '',
    confirmSenha: '',
    telefone: '',
    // tipo de utilizador
    tipo: '',
    // preferência leve para recomendações (opcional)
    areaPreferenciaBasica: 'indiferente', // 'ciencias' | 'letras' | 'indiferente'
    // instituição (quando tipo==='instituicao')
    tipoInstituicao: '',
    descricao: '',
    temTransportePublico: false,
    website: '',
    // convite
    codigoConvite: ''
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);

  // status do código de convite
  const [codigoStatus, setCodigoStatus] = useState({
    validando: false,
    valido: false,
    mensagem: '',
    detalhesInstituicao: null
  });

  const [usandoCodigoConvite, setUsandoCodigoConvite] = useState(false);

  const navigate = useNavigate();
  const { register } = useAuth();

  // forçar tipo estudante quando o código é válido
  useEffect(() => {
    if (codigoStatus.valido) {
      setUsandoCodigoConvite(true);
      setFormData(prev => ({ ...prev, tipo: 'estudante' }));
    } else {
      setUsandoCodigoConvite(false);
    }
  }, [codigoStatus.valido]);

  // validação de senha
  const validatePassword = senha => {
    const minLength = senha.length >= 8;
    const hasUpper = /[A-Z]/.test(senha);
    const hasLower = /[a-z]/.test(senha);
    const hasNumber = /\d/.test(senha);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(senha);
    return {
      valid: minLength && hasUpper && hasLower && hasNumber && hasSpecial,
      message: !minLength
        ? 'A senha deve ter pelo menos 8 caracteres'
        : !hasUpper
        ? 'A senha deve conter pelo menos uma letra maiúscula'
        : !hasLower
        ? 'A senha deve conter pelo menos uma letra minúscula'
        : !hasNumber
        ? 'A senha deve conter pelo menos um número'
        : !hasSpecial
        ? 'A senha deve conter pelo menos um caractere especial'
        : ''
    };
  };

  // verificar código de convite
  const verificarCodigoConvite = async () => {
    if (!formData.codigoConvite.trim()) {
      setCodigoStatus({
        validando: false,
        valido: false,
        mensagem: '',
        detalhesInstituicao: null
      });
      return;
    }
    setCodigoStatus(prev => ({ ...prev, validando: true }));
    try {
      // baseURL do axios `api` já inclui /api; rota pública: GET /api/convites/verificar/:codigo
      const response = await api.get(`/convites/verificar/${formData.codigoConvite}`);
      if (response.data.valido) {
        setCodigoStatus({
          validando: false,
          valido: true,
          mensagem: 'Código válido!',
          detalhesInstituicao: response.data.convite.instituicao
        });
      } else {
        setCodigoStatus({
          validando: false,
          valido: false,
          mensagem: response.data.mensagem || 'Código inválido',
          detalhesInstituicao: null
        });
      }
    } catch (err) {
      console.error('Erro ao verificar código:', err);
      setCodigoStatus({
        validando: false,
        valido: false,
        mensagem: err.response?.data?.mensagem || 'Erro ao verificar código',
        detalhesInstituicao: null
      });
    }
  };

  // auto-check do código após digitar
  useEffect(() => {
    if (formData.codigoConvite.trim().length >= 5) {
      const timeoutId = setTimeout(() => {
        verificarCodigoConvite();
      }, 800);
      return () => clearTimeout(timeoutId);
    }
  }, [formData.codigoConvite]);

  // change handler
  const handleChange = e => {
    const { name, value, checked, type } = e.target;

    if (name === 'codigoConvite' && value !== formData.codigoConvite) {
      setCodigoStatus({
        validando: false,
        valido: false,
        mensagem: '',
        detalhesInstituicao: null
      });
    }

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setError('');
  };

  // submit
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // validações básicas
    if (!formData.nome || !formData.username || !formData.senha) {
      setError('Nome, username e senha são obrigatórios.');
      setLoading(false);
      return;
    }
    const pwdVal = validatePassword(formData.senha);
    if (!pwdVal.valid) {
      setError(pwdVal.message);
      setLoading(false);
      return;
    }
    if (formData.senha !== formData.confirmSenha) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    // tipo obrigatório quando não há convite
    if (!usandoCodigoConvite && !formData.tipo) {
      setError('Tipo de usuário é obrigatório quando não há código de convite.');
      setLoading(false);
      return;
    }

    // validações para instituição
    if (!usandoCodigoConvite && formData.tipo === 'instituicao') {
      if (!formData.tipoInstituicao) {
        setError('O tipo de instituição é obrigatório.');
        setLoading(false);
        return;
      }
      if (!formData.descricao || !formData.telefone) {
        setError('Descrição e telefone são obrigatórios para instituições.');
        setLoading(false);
        return;
      }
    }

    // montar payload leve
    try {
      let dadosRegistro = {
        nome: formData.nome,
        username: formData.username.replace(/_/g, '.'),
        email: formData.email || null,
        senha: formData.senha,
        telefone: formData.telefone || undefined,
        tipo: usandoCodigoConvite ? 'estudante' : formData.tipo,
        metodoCadastro: formData.email ? 'email' : 'username'
      };

      // estudante (sem questionário pesado)
      if ((usandoCodigoConvite && dadosRegistro.tipo === 'estudante') || formData.tipo === 'estudante') {
        // só envia se diferente de "indiferente"
        if (formData.areaPreferenciaBasica && formData.areaPreferenciaBasica !== 'indiferente') {
          dadosRegistro.areaPreferenciaBasica = formData.areaPreferenciaBasica; // backend salva em Usuario.dadosAdicionais
        }
      }

      // instituição
      if (!usandoCodigoConvite && formData.tipo === 'instituicao') {
        dadosRegistro = {
          ...dadosRegistro,
          tipoInstituicao: formData.tipoInstituicao,
          descricao: formData.descricao,
          temTransportePublico: formData.temTransportePublico,
          telefone: formData.telefone,
          website: formData.website
        };
      }

      // convite
      if (codigoStatus.valido) {
        dadosRegistro.codigoConvite = formData.codigoConvite;
      }

      await register(dadosRegistro);
      setSuccessDialog(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erro ao registrar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // campos adicionais
  const renderCamposAdicionais = () => {
    if (formData.tipo === 'estudante') {
      // REGISTO LEVE: só preferência básica (opcional)
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Área de preferência (opcional)
          </Typography>
          <FormControl>
            <RadioGroup
              row
              name="areaPreferenciaBasica"
              value={formData.areaPreferenciaBasica}
              onChange={handleChange}
            >
              <FormControlLabel value="ciencias" control={<Radio />} label="Ciências" />
              <FormControlLabel value="letras" control={<Radio />} label="Letras" />
              <FormControlLabel value="indiferente" control={<Radio />} label="Indiferente" />
            </RadioGroup>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Pode completar o perfil depois para afinar as recomendações.
          </Typography>
        </Box>
      );
    }

    if (formData.tipo === 'instituicao') {
      return (
        <>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Instituição</InputLabel>
            <Select
              name="tipoInstituicao"
              value={formData.tipoInstituicao}
              onChange={handleChange}
              label="Tipo de Instituição"
              disabled={loading}
            >
              <MenuItem value="Universidade">Universidade</MenuItem>
              <MenuItem value="Colégio">Colégio</MenuItem>
              <MenuItem value="Instituto Técnico">Instituto Técnico</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            fullWidth
            multiline
            rows={4}
            label="Descrição"
            name="descricao"
            value={formData.descricao}
            onChange={handleChange}
            disabled={loading}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Telefone"
            name="telefone"
            value={formData.telefone}
            onChange={handleChange}
            disabled={loading}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            disabled={loading}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.temTransportePublico}
                onChange={handleChange}
                name="temTransportePublico"
                disabled={loading}
              />
            }
            label="Possui Transporte Público"
          />
        </>
      );
    }

    return null;
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h5" gutterBottom>
          Registo
        </Typography>

        {/* Código de convite */}
        <Box
          sx={{
            width: '100%',
            my: 2,
            p: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: 2,
            border: codigoStatus.valido ? '1px solid #4caf50' : '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Tem um código de convite?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se recebeu um código de uma instituição, insira-o abaixo para um registo simplificado.
          </Typography>
          <TextField
            fullWidth
            label="Código de Convite"
            name="codigoConvite"
            value={formData.codigoConvite}
            onChange={handleChange}
            variant="outlined"
            disabled={loading}
            error={codigoStatus.mensagem !== '' && !codigoStatus.valido}
            helperText={codigoStatus.mensagem}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={verificarCodigoConvite}
                  disabled={loading || codigoStatus.validando || !formData.codigoConvite}
                  size="small"
                  variant="contained"
                  color="primary"
                  sx={{ ml: 1, minWidth: '90px' }}
                >
                  {codigoStatus.validando ? <CircularProgress size={20} /> : 'Verificar'}
                </Button>
              )
            }}
          />
          {codigoStatus.valido && codigoStatus.detalhesInstituicao && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Código Válido!</AlertTitle>
              Serás vinculado automaticamente a{' '}
              <strong>{codigoStatus.detalhesInstituicao.nome}</strong> após o registo.
            </Alert>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          {!usandoCodigoConvite && (
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Ou registe-se normalmente:
            </Typography>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label="Nome Completo"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            disabled={loading}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            disabled={loading}
            helperText="Use apenas letras, números e pontos"
          />

          <TextField
            margin="normal"
            fullWidth
            label="Email (opcional)"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Telefone (opcional)"
            name="telefone"
            value={formData.telefone}
            onChange={handleChange}
            disabled={loading}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Senha"
            name="senha"
            type="password"
            value={formData.senha}
            onChange={handleChange}
            disabled={loading}
            helperText="Mínimo 8 caracteres, incluindo maiúsculas, minúsculas, números e caracteres especiais"
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Confirmar Senha"
            name="confirmSenha"
            type="password"
            value={formData.confirmSenha}
            onChange={handleChange}
            disabled={loading}
          />

          {/* Tipo de usuário (somente quando não usa convite) */}
          {!usandoCodigoConvite && (
            <FormControl fullWidth margin="normal" disabled={loading}>
              <InputLabel>Tipo de Usuário</InputLabel>
              <Select name="tipo" value={formData.tipo} onChange={handleChange} label="Tipo de Usuário">
                <MenuItem value="estudante">Estudante</MenuItem>
                <MenuItem value="instituicao">Instituição</MenuItem>
              </Select>
            </FormControl>
          )}

          {renderCamposAdicionais()}

          <Button type="submit" fullWidth variant="contained" color="primary" disabled={loading} sx={{ mt: 3, mb: 2 }}>
            {loading
              ? <CircularProgress size={24} />
              : usandoCodigoConvite
                ? 'Registar com Código de Convite'
                : 'Registrar'}
          </Button>
        </Box>
      </Box>

      {/* Diálogo de sucesso */}
      <Dialog
        open={successDialog}
        onClose={() => {
          setSuccessDialog(false);
          navigate('/login');
        }}
      >
        <DialogTitle>Registo Realizado com Sucesso!</DialogTitle>
        <DialogContent>
          <Typography>
            {formData.email
              ? 'Seu registo foi realizado com sucesso! Verifique seu email para activar a conta.'
              : 'Registo realizado! Já podes fazer login.'}
            {codigoStatus.valido && codigoStatus.detalhesInstituicao && (
              <>
                <br />
                <br />
                Foste vinculado automaticamente à instituição {codigoStatus.detalhesInstituicao.nome}.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSuccessDialog(false);
              navigate('/login');
            }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={false} autoHideDuration={6000}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Usuário registado com sucesso!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RegisterPage;
